import React, { useState } from 'react';
import { useSchool } from '../context/SchoolContext';
import { Link } from 'react-router-dom';
import { UserPlus, Users, DollarSign, TrendingDown, TrendingUp, Wallet, ChevronRight } from 'lucide-react';
import './Dashboard.css';
import LeadModal from '../components/LeadModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import AddExpenseModal from '../components/AddExpenseModal';

const Dashboard = () => {
    const { getTodaySummary, leads, stagnantLeads, customers, getPackageName, financials } = useSchool();
    // const { income, expense, balance } = getTodaySummary(); // Replacing with filtered summary
    const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);

    // Date Filtering State
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().setDate(1)).toISOString().split('T')[0], // First day of current month
        end: new Date().toISOString().split('T')[0] // Today
    });

    const filterByDate = (items, dateKey) => {
        if (!items) return [];
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        end.setHours(23, 59, 59, 999);

        return items.filter(item => {
            const date = new Date(item[dateKey] || item.date);
            return date >= start && date <= end;
        });
    };

    const filteredPayments = filterByDate(financials?.payments || [], 'payment_date');
    const filteredExpenses = filterByDate(financials?.expenses || [], 'expense_date');

    const totalIncome = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const totalExpenses = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const variance = totalIncome - totalExpenses;

    const recentLeads = filterByDate(leads || [], 'created_at').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

    // Filter due payments by enrollment date (or other relevant date) AND balance > 0
    const dueCustomers = (customers || []).filter(c => {
        const date = new Date(c.enrollment_date || c.enrollmentDate); // Ensure consistent date field
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        end.setHours(23, 59, 59, 999);
        return c.balance > 0 && date >= start && date <= end;
    }).slice(0, 5);

    const QuickActionCard = ({ title, icon: Icon, colorClass, onClick, to }) => {
        const content = (
            <>
                <div className={`action-icon ${colorClass}`}>
                    <Icon size={24} />
                </div>
                <span className="action-title">{title}</span>
            </>
        );

        if (to) {
            return (
                <Link to={to} className="action-card">
                    {content}
                </Link>
            );
        }

        return (
            <div className="action-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
                {content}
            </div>
        );
    };

    const StatCard = ({ title, amount, icon: Icon, type, subtitle }) => (
        <div className="card stat-card fade-in">
            <div className="stat-header">
                <span className="stat-title">{title}</span>
                <Icon size={20} className={`stat-icon ${type}`} />
            </div>
            <div className="stat-amount">
                ${parseFloat(amount || 0).toFixed(2)}
            </div>
            <span className="stat-sub">{subtitle || 'Selected Period'}</span>
        </div>
    );

    return (
        <div className="dashboard-container">
            {/* Quick Actions */}
            <section className="quick-actions-grid">
                <QuickActionCard
                    title="Add New Lead"
                    icon={UserPlus}
                    colorClass="icon-blue"
                    onClick={() => setIsLeadModalOpen(true)}
                />
                <QuickActionCard
                    title="Customers"
                    icon={Users}
                    colorClass="icon-green"
                    to="/customers"
                />
                <QuickActionCard
                    title="Record Payment"
                    icon={DollarSign}
                    colorClass="icon-gold"
                    onClick={() => setIsPaymentModalOpen(true)}
                />
                <QuickActionCard
                    title="Add Expense"
                    icon={TrendingDown}
                    colorClass="icon-purple"
                    onClick={() => setIsExpenseModalOpen(true)}
                />
            </section>

            {/* Financial Summary */}
            {/* Financial Summary */}
            <section className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg">Financial Overview</h3>
                    <div className="flex gap-2">
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                            className="p-2 border rounded text-sm bg-white"
                        />
                        <span className="self-center">-</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                            className="p-2 border rounded text-sm bg-white"
                        />
                    </div>
                </div>
                <div className="stats-grid">
                    <StatCard title="Income" amount={totalIncome} icon={TrendingUp} type="income" />
                    <StatCard title="Expenses" amount={totalExpenses} icon={TrendingDown} type="expense" />
                    <StatCard title="Variance" amount={variance} icon={Wallet} type={variance >= 0 ? 'income' : 'expense'} subtitle="Net Profit/Loss" />
                </div>
            </section>

            <div className="dashboard-main-grid">
                {/* Due Payments / Debtors */}
                <div className="card list-card fade-in">
                    <div className="card-header">
                        <h3 className="card-title">Due Payments</h3>
                        <Link to="/customers" className="text-xs text-gold font-bold flex items-center gap-1">
                            VIEW ALL <ChevronRight size={12} />
                        </Link>
                    </div>
                    <div className="list-content">
                        {dueCustomers.length === 0 ? (
                            <p className="empty-state">No due payments.</p>
                        ) : (
                            dueCustomers.map(customer => (
                                <Link to={`/customers/${customer.id}`} key={customer.id} className="list-item hover-bg">
                                    <div className="item-info">
                                        <span className="item-name">{customer.name}</span>
                                        <span className="item-sub">{customer.status}</span>
                                    </div>
                                    <div className="item-amount negative">
                                        -${parseFloat(customer.balance || 0).toFixed(2)}
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>
                </div>

                {/* Recent Leads */}
                <div className="card list-card fade-in">
                    <div className="card-header">
                        <h3 className="card-title">Recent Leads</h3>
                        <Link to="/leads" className="text-xs text-gold font-bold flex items-center gap-1">
                            VIEW ALL <ChevronRight size={12} />
                        </Link>
                    </div>
                    <div className="list-content">
                        {recentLeads.length === 0 ? (
                            <p className="empty-state">No recent leads.</p>
                        ) : (
                            recentLeads.map(lead => (
                                <Link to={`/leads/${lead.id}`} key={lead.id} className="list-item hover-bg">
                                    <div className="item-info">
                                        <span className="item-name">{lead.name}</span>
                                        <span className="item-sub">{getPackageName(lead.interest)}</span>
                                    </div>
                                    <span className={`status-badge ${(lead.status || '').toLowerCase()}`}>
                                        {lead.status}
                                    </span>
                                </Link>
                            ))
                        )}
                    </div>
                </div>

                {/* Follow-up Alerts */}
                <div className="card list-card fade-in">
                    <div className="card-header">
                        <h3 className="card-title">Follow-up Alerts</h3>
                        <div className="badge-count" style={{ background: 'var(--accent-red)', color: 'white' }}>
                            {stagnantLeads.length}
                        </div>
                    </div>
                    <div className="list-content">
                        {stagnantLeads.length === 0 ? (
                            <p className="empty-state">All leads are up to date! 🎉</p>
                        ) : (
                            stagnantLeads.slice(0, 5).map(lead => {
                                const days = Math.floor((new Date() - new Date(lead.status_updated_at)) / (1000 * 60 * 60 * 24));
                                return (
                                    <Link to={`/leads/${lead.id}`} key={lead.id} className="list-item hover-bg">
                                        <div className="item-info">
                                            <span className="item-name">{lead.name}</span>
                                            <span className="item-sub">{lead.status} • {days} days stagnant</span>
                                        </div>
                                        <span className="status-badge stagnant">
                                            FOLLOW UP
                                        </span>
                                    </Link>
                                );
                            })
                        )}
                        {stagnantLeads.length > 5 && (
                            <Link to="/leads" className="text-center text-xs text-gold font-bold mt-4 py-2 border-t">
                                VIEW ALL STAGNANT LEADS
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            <LeadModal
                isOpen={isLeadModalOpen}
                onClose={() => setIsLeadModalOpen(false)}
            />

            <RecordPaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
            />

            <AddExpenseModal
                isOpen={isExpenseModalOpen}
                onClose={() => setIsExpenseModalOpen(false)}
            />
        </div>
    );
};

export default Dashboard;
